import { useLingui } from '@lingui/react/macro';
import { fabric } from 'fabric';
import { useEffect, useState } from 'react';
import { toFiniteNumber } from 'shared/utils';
import { round } from 'shared/utils/round';
import history from 'modules/overlay-canvas/addons/history';
import {
  chamferedRectPoints,
  frameOf,
  maxParallelogramAngle,
  parallelogramPoints,
  readChamferCut,
  readParallelogramAngle,
  ShapePoint,
} from 'modules/template-editor/domain/utils/shape-points';
import Input from 'modules/template-editor/ui/components/elements/input';
import { Item, SubItem } from '../styles';
import { useEditorManager } from 'modules/template-editor/ui/context';

interface ShapeParamsProps {
  polygon: fabric.Polygon;
}

/**
 * Параметр формы многоугольника по `data.shapeType`: «Угол скоса» у параллелограмма, «Срез» у прямоугольника со
 * срезанными углами. Точки пересчитываются в той же рамке — ширина и высота фигуры не меняются.
 */
function ShapeParams(props: ShapeParamsProps) {
  const { polygon } = props;
  const shapeType = polygon.data?.shapeType;

  if (shapeType === 'parallelogram') return <SkewAngleField polygon={polygon} />;
  if (shapeType === 'chamfered-rect') return <ChamferField polygon={polygon} />;
  return null;
}

export default ShapeParams;

function SkewAngleField(props: ShapeParamsProps) {
  const { polygon } = props;
  const { t } = useLingui();
  const applyPoints = useApplyPoints(polygon);
  const [angle, setAngle] = useState(() => readAngle(polygon));

  useEffect(() => setAngle(readAngle(polygon)), [polygon]);

  const onChange = (value: string) => {
    const frame = frameOf(polygon.points ?? []);
    const max = maxParallelogramAngle(frame, scaleOf(polygon));
    const next = round(Math.min(Math.max(toFiniteNumber(value, angle), -max), max));
    if (next === angle) return;
    applyPoints(parallelogramPoints(frame, next, scaleOf(polygon)));
    setAngle(next);
  };

  return (
    <Item>
      <SubItem style={{ justifyContent: 'space-between' }}>
        <span>{t`Угол скоса`}</span>
        <Input numeric unit={'°'} style={{ width: 127 }} value={angle.toString()} onSubmit={onChange} onBlur={onChange} />
      </SubItem>
    </Item>
  );
}

function ChamferField(props: ShapeParamsProps) {
  const { polygon } = props;
  const { t } = useLingui();
  const applyPoints = useApplyPoints(polygon);
  const [cut, setCut] = useState(() => readCut(polygon));

  useEffect(() => setCut(readCut(polygon)), [polygon]);

  const onChange = (value: string) => {
    const frame = frameOf(polygon.points ?? []);
    const scale = scaleOf(polygon);
    // Срез на экране один по обеим осям: не больше половины меньшей стороны, иначе у растянутой фигуры он перекосится.
    const max = Math.min(frame.width * scale.x, frame.height * scale.y) / 2;
    const next = round(Math.min(Math.max(toFiniteNumber(value, cut), 0), max));
    if (next === cut) return;
    applyPoints(chamferedRectPoints(frame, next / scale.x, next / scale.y));
    setCut(next);
  };

  return (
    <Item>
      <SubItem style={{ justifyContent: 'space-between' }}>
        <span>{t`Срез углов`}</span>
        <Input numeric unit={'px'} style={{ width: 127 }} value={cut.toString()} onSubmit={onChange} onBlur={onChange} />
      </SubItem>
    </Item>
  );
}

/** Записать новые точки той же рамки: `pathOffset` и размер Fabric не пересчитывает — рамка их и не меняет. */
function useApplyPoints(polygon: fabric.Polygon) {
  const manager = useEditorManager();
  return (points: ShapePoint[]) => {
    polygon.set({ points: points.map((point) => new fabric.Point(point.x, point.y)) });
    polygon.setCoords();
    manager?.render();
    history.saveState();
  };
}

/** Угол скоса на экране, целые градусы. */
function readAngle(polygon: fabric.Polygon) {
  return round(readParallelogramAngle(polygon.points ?? [], scaleOf(polygon)));
}

/** Срез на экране, px. */
function readCut(polygon: fabric.Polygon) {
  return round(readChamferCut(polygon.points ?? []) * scaleOf(polygon).x);
}

function scaleOf(polygon: fabric.Polygon) {
  return { x: polygon.scaleX || 1, y: polygon.scaleY || 1 };
}
